import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { ArrowLeft, Download, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import html2pdf from "html2pdf.js";

// Helper para formatear fechas
const formatDate = (dateStr) => {
  if (!dateStr) return "";
  try {
    const [year, month, day] = dateStr.split("-").map(Number);
    const date = new Date(year, month - 1, day);
    return format(date, "dd/MM/yyyy", { locale: es });
  } catch {
    return dateStr;
  }
};

// Helper para formatear números con separador de miles español
const formatNumber = (num, decimals = 2) => {
  if (!num && num !== 0) return "";
  return Number(num).toLocaleString("es-ES", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

export default function InformeCso() {
  const { id } = useParams();
  const navigate = useNavigate();
  const reportRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/batches/${id}/informe-cso`);
      setData(response.data);
    } catch (error) {
      console.error("Error fetching informe CSO data:", error);
      toast.error("Error al cargar los datos del informe");
      navigate(`/batches/${id}`);
    } finally {
      setLoading(false);
    }
  };

  const generatePdf = async () => {
    if (!reportRef.current) return;
    
    // Validar que tenemos los datos necesarios
    if (!data?.lote?.numero_lote || !data?.lote?.id) {
      toast.error("Error: No se encontraron los datos del lote");
      console.error("Datos del lote incompletos:", data);
      return;
    }
    
    setGenerating(true);
    try {
      const element = reportRef.current;
      const lote = data.lote;
      const batchId = lote.id; // Usar el ID del lote desde los datos, no desde useParams
      const filename = `CSO_${lote.contenedor || lote.numero_lote || "informe"}`;
      const opt = {
        margin: [3, 3, 3, 3],
        filename: `${filename}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      };
      
      // Generar el PDF como blob
      const pdfBlob = await html2pdf().set(opt).from(element).outputPdf('blob');
      
      // Convertir blob a array buffer para poder reutilizarlo
      const pdfArrayBuffer = await pdfBlob.arrayBuffer();
      
      // Función para subir el documento
      const uploadDocument = async (overwrite = false) => {
        // Crear nuevo blob desde el array buffer
        const newBlob = new Blob([pdfArrayBuffer], { type: 'application/pdf' });
        const formData = new FormData();
        formData.append('file', newBlob, `${filename}.pdf`);
        formData.append('contenedor', lote.contenedor || '');
        formData.append('awb', lote.awb_aereo || '');
        formData.append('bl', lote.bl_maritimo || '');
        formData.append('numero_lote', lote.numero_lote);
        formData.append('nombre_archivo', `${filename}.pdf`);
        formData.append('tipo_documento', 'cso');
        formData.append('batch_id', batchId);
        if (overwrite) {
          formData.append('overwrite', 'true');
        }

        console.log("Enviando documento:", {
          batch_id: batchId,
          numero_lote: lote.numero_lote,
          contenedor: lote.contenedor,
          tipo_documento: 'cso',
          overwrite
        });

        return api.post('/documents/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      };
      
      // Guardar en el servidor
      try {
        console.log("Intentando subir CSO con datos:", {
          batch_id: id,
          numero_lote: lote.numero_lote,
          contenedor: lote.contenedor,
          overwrite: false
        });
        
        const response = await uploadDocument(false);
        console.log("Respuesta del servidor:", response.data);
        
        // Si es duplicado, preguntar si sobrescribir
        if (response.data?.status === 'duplicate') {
          const existingDoc = response.data.document;
          const confirmOverwrite = window.confirm(
            `Ya existe un documento CSO para este lote:\n\n` +
            `Archivo: ${existingDoc?.nombre_archivo || 'CSO'}\n` +
            `Subido por: ${existingDoc?.subido_por_nombre || 'Sistema'}\n\n` +
            `¿Desea sobrescribir el documento existente?`
          );
          
          if (confirmOverwrite) {
            console.log("Usuario confirmó sobrescribir, enviando con overwrite=true");
            const overwriteResponse = await uploadDocument(true);
            console.log("Respuesta sobrescritura:", overwriteResponse.data);
            
            if (overwriteResponse.data?.id) {
              toast.success("CSO guardado correctamente (sobrescrito)");
            } else {
              toast.error("Error: no se pudo sobrescribir el documento");
              console.error("Respuesta inesperada:", overwriteResponse.data);
            }
          } else {
            toast.info("Operación cancelada");
          }
        } else if (response.data?.id) {
          toast.success("CSO guardado correctamente");
        } else {
          console.error("Respuesta inesperada:", response.data);
        }
      } catch (uploadError) {
        console.error("Error guardando en servidor:", uploadError);
        toast.error("Error al guardar el documento: " + (uploadError.response?.data?.detail || uploadError.message));
      }
      
      // Descargar localmente
      const downloadBlob = new Blob([pdfArrayBuffer], { type: 'application/pdf' });
      const url = URL.createObjectURL(downloadBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${filename}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success("PDF generado correctamente");
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Error al generar el PDF");
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">No se encontraron datos para el informe</p>
        <Button variant="outline" onClick={() => navigate(`/batches/${id}`)} className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver al lote
        </Button>
      </div>
    );
  }

  const { lote, productor, empresa_importadora, proveedor, lugar_destino, puesto_control, producto } = data;

  // Estilos para las celdas
  const cellStyle = "border border-slate-400 p-1 text-[8px]";
  const labelStyle = "text-slate-500 text-[7px] uppercase";
  const valueStyle = "font-semibold text-[9px]";
  const checkboxStyle = "w-3 h-3 border border-slate-400 inline-flex items-center justify-center mr-1";

  return (
    <div className="space-y-6" data-testid="informe-cso-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(`/batches/${id}`)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Revisión CSO y Captura</h1>
            <p className="text-slate-500">Lote: {lote?.numero_lote}</p>
          </div>
        </div>
        <Button onClick={generatePdf} disabled={generating}>
          {generating ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Generando...
            </>
          ) : (
            <>
              <Download className="w-4 h-4 mr-2" />
              Descargar PDF
            </>
          )}
        </Button>
      </div>

      {/* Preview Card */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Vista previa del certificado
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto bg-slate-100 p-4">
          {/* Document Preview - Replicando formato oficial */}
          <div 
            ref={reportRef} 
            className="bg-white mx-auto shadow-lg"
            style={{ width: "210mm", fontSize: "9px", fontFamily: "Arial, sans-serif", padding: "8mm" }}
          >
            {/* NÚMERO DE CONTENEDOR DESTACADO */}
            <div className="text-center mb-4 bg-blue-600 text-white py-3 rounded">
              <div className="text-2xl font-bold tracking-wider">{lote?.contenedor || "SIN CONTENEDOR"}</div>
              <div className="text-sm mt-1">CONTENEDOR</div>
            </div>

            {/* Header del documento */}
            <div className="flex justify-between items-start border-b-2 border-green-700 pb-2 mb-2">
              <div>
                <span className={labelStyle}>PAÍS: </span>
                <span className={valueStyle}>{productor?.pais || lote?.pais_origen || "Venezuela"}</span>
              </div>
              <div className="text-center">
                <div className="text-green-800 font-bold text-lg">Certificado zoosanitario-oficial para la UE</div>
                <div className="text-slate-500 text-xs">Parte I: Descripción de la partida</div>
              </div>
              <div className="text-right">
                <span className={labelStyle}>Forma GTVC 19-1/L214/11</span>
              </div>
            </div>

            {/* Grid principal - 2 columnas */}
            <div className="grid grid-cols-2 gap-0">
              
              {/* ===== COLUMNA IZQUIERDA ===== */}
              <div className="border-r border-slate-400">
                
                {/* 1.1 Expedidor/Exportador */}
                <div className={cellStyle}>
                  <div className={labelStyle}>1.1 Expedidor/Exportador</div>
                  <div className={valueStyle}>{proveedor?.nombre || lote?.proveedor_nombre || ""}</div>
                  <div className="text-[8px]">{proveedor?.direccion || ""}</div>
                  <div className="mt-1">
                    <span className={labelStyle}>País: </span>
                    <span>{productor?.pais || lote?.pais_origen || "Venezuela"}</span>
                    <span className="ml-2"><span className={labelStyle}>Código ISO: </span></span>
                    <span>{productor?.codigo_iso || "VE"}</span>
                  </div>
                </div>

                {/* 1.7 País de origen */}
                <div className={cellStyle}>
                  <div className={labelStyle}>1.7 País de origen</div>
                  <div className="flex justify-between">
                    <div>
                      <span className={labelStyle}>País: </span>
                      <span className={valueStyle}>{productor?.pais || lote?.pais_origen || "Venezuela"}</span>
                    </div>
                    <div>
                      <span className={labelStyle}>Código ISO: </span>
                      <span className={valueStyle}>{productor?.codigo_iso || "VE"}</span>
                    </div>
                  </div>
                </div>

                {/* 1.11 Lugar de expedición */}
                <div className={cellStyle}>
                  <div className={labelStyle}>1.11 Lugar de expedición</div>
                  <div className={valueStyle}>{productor?.nombre || "INVERSIONES FRISUR, C.A."} {productor?.spes || "SPES-6479"}</div>
                  <div className="text-[8px]">{productor?.direccion || ""}</div>
                  <div className="mt-1 flex justify-between">
                    <div>
                      <span className={labelStyle}>Nº Registro: </span>
                      <span>{productor?.spes?.replace("SPES-", "") || "6479"}</span>
                    </div>
                    <div>
                      <span className={labelStyle}>País: </span>
                      <span>{productor?.pais || "Venezuela"}</span>
                    </div>
                    <div>
                      <span className={labelStyle}>ISO: </span>
                      <span>{productor?.codigo_iso || "VE"}</span>
                    </div>
                  </div>
                </div>

                {/* 1.13 Lugar de carga */}
                <div className={cellStyle}>
                  <div className={labelStyle}>1.13 Lugar de carga</div>
                  <div className={valueStyle}>PUERTO DE LA GUAIRA</div>
                  <div className="flex justify-between">
                    <div>
                      <span className={labelStyle}>País: </span>
                      <span>Venezuela</span>
                    </div>
                    <div>
                      <span className={labelStyle}>Código ISO: </span>
                      <span>VE</span>
                    </div>
                  </div>
                </div>

                {/* 1.14 Fecha y hora de salida */}
                <div className={cellStyle}>
                  <div className={labelStyle}>1.14 Fecha y hora de salida</div>
                  <div className={valueStyle}>{formatDate(lote?.salida_buque)} 00:00:00</div>
                </div>

                {/* 1.17 Documentos de acompañamiento */}
                <div className={cellStyle}>
                  <div className={labelStyle}>1.17 Documentos de acompañamiento</div>
                  <div className="grid grid-cols-2 gap-1 mt-1">
                    <div>
                      <span className={labelStyle}>Tipo: </span>
                      <span>FACTURA COMERCIAL</span>
                    </div>
                    <div>
                      <span className={labelStyle}>País: </span>
                      <span>{productor?.pais || "Venezuela"}</span>
                    </div>
                  </div>
                  <div className="mt-1">
                    <span className={labelStyle}>Referencia documento comercial: </span>
                    <span className={valueStyle}>{lote?.factura_origen || ""}</span>
                  </div>
                </div>

                {/* 1.19 Número del recipiente/precinto */}
                <div className={cellStyle}>
                  <div className={labelStyle}>1.19 Número del recipiente / Número de precinto</div>
                  <div className="mt-1">
                    <span className={labelStyle}>Número del recipiente: </span>
                    <span className={valueStyle}>{lote?.contenedor || ""}</span>
                  </div>
                  <div>
                    <span className={labelStyle}>Número del precinto: </span>
                    <span className={valueStyle}>{lote?.precintos || ""}</span>
                  </div>
                </div>

                {/* 1.24 Número total de bultos */}
                <div className={cellStyle}>
                  <div className={labelStyle}>1.24 Número total de bultos</div>
                  <div className="text-xl font-bold text-center">{lote?.bultos || 0}</div>
                </div>

              </div>

              {/* ===== COLUMNA DERECHA ===== */}
              <div>

                {/* 1.5 Destinatario/Importador */}
                <div className={cellStyle}>
                  <div className={labelStyle}>1.5 Destinatario/Importador</div>
                  <div className={valueStyle}>{empresa_importadora?.nombre || "DEEPBLUE SEAFOOD SL"}</div>
                  <div className="text-[8px]">{empresa_importadora?.direccion || ""}</div>
                  <div className="mt-1">
                    <span className={labelStyle}>País: </span>
                    <span>ESPAÑA</span>
                    <span className="ml-2"><span className={labelStyle}>Código ISO: </span></span>
                    <span>ES</span>
                  </div>
                </div>

                {/* 1.9 País de destino */}
                <div className={cellStyle}>
                  <div className={labelStyle}>1.9 País de destino</div>
                  <div className="flex justify-between">
                    <div>
                      <span className={labelStyle}>País: </span>
                      <span className={valueStyle}>ESPAÑA</span>
                    </div>
                    <div>
                      <span className={labelStyle}>Código ISO: </span>
                      <span className={valueStyle}>ES</span>
                    </div>
                  </div>
                </div>

                {/* 1.12 Lugar de destino */}
                <div className={cellStyle}>
                  <div className={labelStyle}>1.12 Lugar de destino</div>
                  <div className={valueStyle}>{lugar_destino?.nombre || "FRIO CEREZO, S.L."}</div>
                  <div className="text-[8px]">{lugar_destino?.direccion || "C/ PESCADOR 37 46293 BENEIXIDA VALENCIA"}</div>
                  <div className="mt-1 grid grid-cols-2 gap-1">
                    <div>
                      <span className={labelStyle}>Nº Registro: </span>
                      <span>{lugar_destino?.numero_registro || "40.27241/V"}</span>
                    </div>
                    <div>
                      <span className={labelStyle}>CIF: </span>
                      <span>{lugar_destino?.cif || "B97292148"}</span>
                    </div>
                    <div>
                      <span className={labelStyle}>Teléfono: </span>
                      <span>{lugar_destino?.telefono || "962580800"}</span>
                    </div>
                    <div>
                      <span className={labelStyle}>País: </span>
                      <span>{lugar_destino?.pais || "España"}</span>
                      <span className="ml-1">({lugar_destino?.codigo_iso_pais || "ES"})</span>
                    </div>
                  </div>
                </div>

                {/* 1.15 Medios de transporte */}
                <div className={cellStyle}>
                  <div className={labelStyle}>1.15 Medios de transporte</div>
                  <div className="flex flex-wrap gap-2 mt-1">
                    <span className="inline-flex items-center">
                      <span className={checkboxStyle}>
                        {lote?.tipo_envio === "aereo" && "✓"}
                      </span>
                      Aeronave
                    </span>
                    <span className="inline-flex items-center">
                      <span className={checkboxStyle}></span>
                      Ferrocarril
                    </span>
                    <span className="inline-flex items-center">
                      <span className={checkboxStyle}>
                        {lote?.tipo_envio === "maritimo" && "✓"}
                      </span>
                      Buque
                    </span>
                    <span className="inline-flex items-center">
                      <span className={checkboxStyle}></span>
                      Vehículo
                    </span>
                  </div>
                  <div className="mt-1">
                    <span className={labelStyle}>Identificación Buque: </span>
                    <span className={valueStyle}>{lote?.identificacion_buque || ""}</span>
                  </div>
                </div>

                {/* 1.16 Puesto de control fronterizo */}
                <div className={cellStyle}>
                  <div className={labelStyle}>1.16 Puesto de control fronterizo de entrada</div>
                  <div className={valueStyle}>{puesto_control?.nombre || "ESVLC1-Animal Health"}</div>
                </div>

                {/* 1.18 Condiciones de transporte */}
                <div className={cellStyle}>
                  <div className={labelStyle}>1.18 Condiciones de transporte</div>
                  <div className="flex gap-3 mt-1">
                    <span className="inline-flex items-center">
                      <span className={checkboxStyle}></span>
                      Ambiente
                    </span>
                    <span className="inline-flex items-center">
                      <span className={checkboxStyle}></span>
                      Refrigeración
                    </span>
                    <span className="inline-flex items-center">
                      <span className={checkboxStyle}>✓</span>
                      Congelación
                    </span>
                  </div>
                </div>

                {/* 1.20 Certificados como */}
                <div className={cellStyle}>
                  <div className={labelStyle}>1.20 Certificados como o a efectos de:</div>
                  <div className="grid grid-cols-2 gap-1 mt-1">
                    <span className="inline-flex items-center">
                      <span className={checkboxStyle}>✓</span>
                      Productos consumo humano
                    </span>
                    <span className="inline-flex items-center">
                      <span className={checkboxStyle}></span>
                      Industria conservera
                    </span>
                    <span className="inline-flex items-center">
                      <span className={checkboxStyle}></span>
                      Animales acuáticos vivos
                    </span>
                    <span className="inline-flex items-center">
                      <span className={checkboxStyle}></span>
                      Transformación ulterior
                    </span>
                  </div>
                </div>

                {/* 1.25 y 1.26 */}
                <div className={cellStyle}>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <div className={labelStyle}>1.25 Cantidad total</div>
                      <div className="text-xl font-bold text-center">{lote?.bultos || 0}</div>
                    </div>
                    <div>
                      <div className={labelStyle}>1.26 Peso neto / Peso bruto (kg)</div>
                      <div className="text-xl font-bold text-center">
                        {formatNumber(lote?.peso_neto)} / {formatNumber(lote?.peso_bruto)}
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            </div>

            {/* 1.27 Descripción de la partida - Ancho completo */}
            <div className={`${cellStyle} mt-0`}>
              <div className={labelStyle}>1.27 Descripción de la partida</div>
              
              {/* Fila 1: Código NC, Especie, Almacén frigorífico, Tipos de embalaje, Naturaleza mercancía */}
              <div className="grid grid-cols-5 gap-2 mt-2 border-b border-slate-200 pb-2">
                <div>
                  <div className={labelStyle}>Código NC</div>
                  <div className={valueStyle}>{producto?.codigo_arancelario || lote?.codigo_arancelario || ""}</div>
                </div>
                <div>
                  <div className={labelStyle}>Especie</div>
                  <div className={valueStyle}>{producto?.nombre || lote?.producto_nombre || ""}</div>
                  <div className="text-[7px] italic">{producto?.nombre_cientifico || lote?.nombre_cientifico || ""}</div>
                </div>
                <div>
                  <div className={labelStyle}>Almacén frigorífico</div>
                  <div className={valueStyle}>{productor?.nombre || "Inversiones Frisur, C.A."}</div>
                  <div className="text-[7px]">{productor?.spes || "SPES-6479"} ({productor?.codigo_iso || "VE"})</div>
                </div>
                <div>
                  <div className={labelStyle}>Tipos de embalaje</div>
                  <div className={valueStyle}>CAJAS DE CARTON</div>
                </div>
                <div>
                  <div className={labelStyle}>Naturaleza mercancía fábrica/planta procesadora</div>
                  <div className={valueStyle}>PESCA SILVESTRE</div>
                  <div className="text-[7px]">{productor?.nombre || "Inversiones Frisur, C.A."} {productor?.spes || "SPES-6479"} ({productor?.codigo_iso || "VE"})</div>
                </div>
              </div>

              {/* Fila 2: Consumidor final, Tipo tratamiento, Fecha recogida, Peso neto, Número bultos, Número lote */}
              <div className="grid grid-cols-6 gap-2 mt-2">
                <div>
                  <div className={labelStyle}>Consumidor final</div>
                  <span className="inline-flex items-center">
                    <span className={checkboxStyle}></span>
                  </span>
                </div>
                <div>
                  <div className={labelStyle}>Tipo de tratamiento</div>
                  <div className={valueStyle}>CONGELADO</div>
                </div>
                <div>
                  <div className={labelStyle}>Fecha de recogida / producción</div>
                  <div className={valueStyle}>{formatDate(lote?.fecha_captura)}</div>
                </div>
                <div>
                  <div className={labelStyle}>Peso neto</div>
                  <div className={valueStyle}>{formatNumber(lote?.peso_neto)}</div>
                </div>
                <div>
                  <div className={labelStyle}>Número de bultos</div>
                  <div className={valueStyle}>{lote?.bultos || 0}</div>
                </div>
                <div>
                  <div className={labelStyle}>Número de lote</div>
                  <div className={valueStyle}>{lote?.numero_lote || ""}</div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="mt-4 pt-2 border-t border-slate-300 flex justify-between text-[7px] text-slate-400">
              <div>Forma GTVC 19-1/L214/11</div>
              <div>Generado desde Sistema de Gestión Premium Fish - {format(new Date(), "dd/MM/yyyy HH:mm", { locale: es })}</div>
              <div>1 DE 1</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
